<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <responsiveButton :setText="true"
                                  setColor="orange"
                                  setDefaultIcon="mdi-refresh"
                                  setSmallIcon="mdi-refresh"
                                  :setClick="initialize"
                >
                    Refresh Data
                </responsiveButton>
                <v-divider class="mx-6"></v-divider>
                <v-data-table
                    :headers="clientsTable.headers"
                    :items="clients"
                    :loading="loading"
                    :options.sync="pagination"
                    :server-items-length="totalItems"
                    loading-text="Loading... Please wait"
                    class="elevation-1">

                    <template v-slot:item.name="{item}">
                        {{ `${item.name} ${item.family_name}` }}
                    </template>

                    <template v-slot:item.status="{item}">
                        <v-chip class="mr-4"
                                :color="employeeClientStatus[item.status].color"
                                dark>
                            {{ employeeClientStatus[item.status].text}}
                        </v-chip>
                    </template>

                    <template v-slot:item.authorizationStatus="{item}">
                        <v-chip class="mr-4"
                                :color="!!!item.authorizationRecord?'':authorizationStatus[item.authorizationRecord.status].color"
                                dark>
                            {{ !!!item.authorizationRecord? 'None': item.authorizationRecord.status}}
                        </v-chip>
                    </template>

                    <template v-slot:item.authorizationDaysLeft="{item}">
                        {{ getAuthorizationDaysText(item) }}
                    </template>

                    <!-- actions -->
                    <template v-slot:item.view="{item}">
                        <v-icon
                                class="mr-2"
                                @click="viewClient(item)"
                        >
                            {{ CLIENT_ACTIONS.VIEW.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.profile="{item}">
                        <v-icon
                                class="mr-2"
                                @click="editClient(item)"
                        >
                            {{ CLIENT_ACTIONS.EDIT.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.documents="{item}">
                        <v-icon
                                class="mr-2"
                                @click="editClientDocuments(item)"
                        >
                            {{ CLIENT_ACTIONS.EDIT_DOCUMENTS.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.authorizationIcon="{item}">
                        <v-icon
                                class="mx-auto"
                                @click="authorizationView(item)"
                        >
                            {{ CLIENT_ACTIONS.AUTORIZATIONS.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.remove="{item}">
                        <v-icon
                                class="mx-auto"
                                @click="confirmDelete(item)"
                        >
                            {{ CLIENT_ACTIONS.DELETE.icon }}
                        </v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <dialog-alert
                :text="confirmAlertText"
                :alert-type="confirmAlertType"
                :open="openConfirmation"
                @alert-closed="dialogClosed($event)"
        ></dialog-alert>
    </v-container>
</template>

<script>
    import alertEventBus from '@/eventBus/AlertEventBus';
    import {EMPLOYEE_CLIENT_STATUS, AUTHORIZATION_STATUS} from '@/shared/enums/EntitiesStatus'
    import {CLIENT_ACTIONS} from "@/shared/enums/Client";
    const responsiveButton = () => import('@/components/common/mobile/Button');
    const DialogAlert = () => import("@/components/common/DialogAlert");
    import { ALERT_TYPES, CONFIRM_DELETE } from "@/shared/enums/AlertTexts";
    import {DOCUMENT_FILE_STATUS} from "@/shared/enums/DocumentFile";
    import {DOCUMENT_RULE_CONFIGURATIONS} from "@/shared/enums/DocumentRule";

    export default {
        name: "clientTable",
        components: { responsiveButton, DialogAlert },
        data(){
            return {
                clients: [],
                loading: true,
                clientsTable: {
                    headers: [
                        {
                            text: 'Name',
                            align: 'left',
                            sortable: true,
                            value: 'name'
                        },
                        {
                            text: 'Category',
                            align: 'left',
                            sortable: true,
                            value: 'typeName'
                        },
                        {
                            text: 'Status',
                            align: 'left',
                            sortable: true,
                            value: 'status',
                        },
                        {
                            text: 'Authorization Status',
                            align: 'left',
                            sortable: true,
                            value: 'authorizationStatus'
                        },
                        {
                            text: 'Authorization Days left',
                            align: 'left',
                            sortable: true,
                            value: 'authorizationDaysLeft',
                            divider: true
                        },
                        {
                            text: 'View',
                            align: 'center',
                            width: '2%',
                            value: 'view',
                            sortable: false
                        },
                        {
                            text: 'Profile',
                            align: 'center',
                            width: '2%',
                            value: 'profile',
                            sortable: false
                        },
                        {
                            text: 'Edit Category',
                            align: 'center',
                            width: '8%',
                            value: 'documents',
                            sortable: false
                        },
                        {
                            text: 'Authorization',
                            align: 'center',
                            width: '2%',
                            value: 'authorizationIcon',
                            sortable: false
                        },
                        {
                            text: 'Remove',
                            align: 'center',
                            width: '2%',
                            value: 'remove',
                            sortable: false
                        }
                    ]
                },
                documentFileStatus: DOCUMENT_FILE_STATUS,
                authorizationStatus: AUTHORIZATION_STATUS,
                employeeClientStatus: EMPLOYEE_CLIENT_STATUS,
                confirmAlertType: ALERT_TYPES.CONFIRM,
                confirmAlertText: CONFIRM_DELETE,
                openConfirmation: false,
                clientToDelete: null,
                //pagination properties
                totalItems: 0,
                pagination: {},
                pageKeyCache: new Map()
            };
        },
        watch: {
            pagination: {
                async handler () {
                    await this.getClients();
                },
                deep: true
            }
        },
        computed:{
            CLIENT_ACTIONS(){
                return CLIENT_ACTIONS;
            }
        },
        methods:{
            initialize() {
                this.getClients();
            },
            getAuthorizationDaysText(item){
                if(!!!item.authorizationRecord)
                    return 'None';

                return item.authorizationRecord.timePeriod == 999999999 ? DOCUMENT_RULE_CONFIGURATIONS.EXPIRATION.NO_EXPIRE_TEXT : item.authorizationRecord.timePeriod;
            },
            async getClients() {
                this.loading = true;
                let params = this.getPaginationValues();

                if(!params.limit)
                    return;

                try{
                    await this.$store.dispatch('getClients', params);
                    this.clients = this.$store.getters.getClients;
                }
                catch (e) {
                    //set alert
                    this.loading = false;
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    return;
                }

                this.setPaginationValues(this.clients);
                this.loading = false;
            },
            getPaginationValues(){
                //all values of pagination that can be used for backend
                const { page, itemsPerPage } = this.pagination;
                let params = {
                    limit: itemsPerPage
                }

                if(this.pageKeyCache.has(page) && page != 1){
                    params.lastEvaluatedKey = this.pageKeyCache.get(page);
                }
                else {
                    //we have not been there yet, so it needs to be to cases 1: this is brand new search page 0
                    // or it is the next page that we has not seem
                    if(page != 1) {
                        params.lastEvaluatedKey = this.pageKeyCache.get(page-1);
                    }
                }
                return params;
            },
            setPaginationValues(list){
                let key = this.$store.getters.getClientsPageKey;
                const {page} = this.pagination;

                if(key != null && Object.keys(key).length > 0){
                    this.pageKeyCache.set(page, key);
                    this.totalItems = list.length +1;
                }
                else{
                    this.totalItems = list.length;
                }
            },
            async editClient(client){
                this.$store.commit('setClient',client);
                this.$router.push({name: CLIENT_ACTIONS.EDIT.route, params:{id: client.id}});
            },
            async viewClient(client){
                this.$store.commit('setClient',client);
                this.$router.push({name: CLIENT_ACTIONS.VIEW.route, params:{id: client.id}});

            },
            async editClientDocuments(client){
                this.$store.commit('setClient',client);
                this.$router.push({name: CLIENT_ACTIONS.EDIT_DOCUMENTS.route, params:{id: client.id}});
            },
            authorizationView(client){
                this.$store.commit('setClient',client);
                this.$router.push({name: CLIENT_ACTIONS.AUTORIZATIONS.route, params:{path_id: client.id}});
            },
            async deleteClient(client) {
                const index = this.clients.indexOf(client);


                try {
                    await this.$store.dispatch("deleteClient", {
                        id: client.id
                    });
                } catch (e) {
                    alertEventBus.setAlert({ type: "error", msg: e.message });
                    return;
                }

                this.clients.splice(index, 1);
                this.clientToDelete = null;
            },
            confirmDelete(item) {
                this.openConfirmation = true;
                this.clientToDelete = item;
            },
            dialogClosed(answer) {
                this.openConfirmation = false;
                if (answer) {
                    this.deleteClient(this.clientToDelete);
                }
            }
        },
        async created(){
            this.initialize();
        }
    }
</script>

<style>
    .v-chip.white-span span {
        color: white !important;
    }
</style>